import { Action, createReducer, on } from '@ngrx/store';
import * as LabsActions from '../actions';
import { ILab, ILabCreate, ILabProjectUserList, LabStatus } from '../../models/Labs';
import { isLabRetired } from '../../../../shared/utils/lab-status';

export interface LabsState {
  labList: ILab[];
  // Archiving, archived and deleting labs for the platform admin Labs page.
  // Kept apart from labList, which feeds the side nav and every lab picker.
  archivedLabList: ILab[];
  selectedLab: ILab | null;
  postLab: ILabCreate | null;
  getLabDetail: ILab | null;
  getLabProjectUsersList: ILabProjectUserList[];
}

const initialState: LabsState = {
  labList: [],
  archivedLabList: [],
  selectedLab: null,
  postLab: null,
  getLabDetail: null,
  getLabProjectUsersList: [],
};

export const labsReducer = createReducer(
  initialState,
  //get labs
  on(LabsActions.LabsApiActions.GetLabsSuccess, (state, { payload }) => ({
    ...state,
    labList: payload,
    // Re-resolve the active lab against the list the server just returned. A
    // selection carried in from localStorage, whether another account's or a
    // lab this user was removed from, is dropped, and a renamed lab picks up
    // its current display_name instead of the stale persisted one.
    selectedLab: state.selectedLab
      ? (payload.find((lab) => lab.id === state.selectedLab?.id) ?? null)
      : null,
  })),
  on(LabsActions.LabsApiActions.GetLabsFailure, (state) => ({
    ...state,
  })),
  // The include-archived list carries every lab; keep only the retired ones.
  on(LabsActions.LabsApiActions.GetArchivedLabsSuccess, (state, { payload }) => ({
    ...state,
    archivedLabList: payload.filter((lab) => isLabRetired(lab)),
  })),
  on(LabsActions.LabsApiActions.GetArchivedLabsFailure, (state) => ({
    ...state,
  })),
  //select lab
  on(LabsActions.LabsUiActions.SelectLab, (state, { lab }) => ({
    ...state,
    selectedLab: lab,
  })),
  on(LabsActions.LabsUiActions.ClearSelectedLab, (state) => ({
    ...state,
    selectedLab: null,
  })),
  //post lab
  on(LabsActions.LabsApiActions.PostLabSuccess, (state, { payload }) => ({
    ...state,
    postLab: payload,
  })),
  on(LabsActions.LabsApiActions.PostLabFailure, (state) => ({
    ...state,
  })),
  //lab detail
  // Clear getLabDetail when navigating to a different lab so the UI never
  // shows stale data from the previously-viewed lab while the new lab's
  // GET /api/labs/:id response is still in flight. Without this, components
  // bound to getLabDetailSignal (lab-details, breadcrumb, sidenav projects
  // submenu) display the previous lab until the new fetch completes.
  on(LabsActions.LabsUiActions.GetLabDetail, (state, { labId }) => ({
    ...state,
    getLabDetail: state.getLabDetail?.id === labId ? state.getLabDetail : null,
  })),
  on(LabsActions.LabsApiActions.GetLabDetailSuccess, (state, { payload }) => ({
    ...state,
    getLabDetail: payload,
  })),
  on(LabsActions.LabsApiActions.GetLabDetailFailure, (state) => ({
    ...state,
  })),

  //lab patch
  on(LabsActions.LabsUiActions.PatchLab, (state) => ({
    ...state,
  })),
  on(LabsActions.LabsApiActions.PatchLabSuccess, (state) => ({
    ...state,
  })),
  //lab project users list
  on(LabsActions.LabsUiActions.GetLabProjectUsersList, (state) => ({
    ...state,
    getLabProjectUsersList: [], // Clear previous data when fetching new lab's users
  })),
  on(LabsActions.LabsApiActions.GetLabProjectUsersListSuccess, (state, { payload }) => ({
    ...state,
    getLabProjectUsersList: payload,
  })),
  on(LabsActions.LabsApiActions.GetLabProjectUsersListFailure, (state) => ({
    ...state,
  })),

  // lab user assign
  on(LabsActions.LabsUiActions.PostAssignLabUser, (state) => ({
    ...state,
  })),
  on(LabsActions.LabsApiActions.PostAssignLabUserSuccess, (state) => ({
    ...state,
  })),
  on(LabsActions.LabsApiActions.PostAssignLabUserFailure, (state) => ({
    ...state,
  })),
  on(LabsActions.LabsUiActions.DeleteLabUser, (state) => ({
    ...state,
  })),

  // Deactivating and reactivating keep the lab in the list.
  on(LabsActions.LabsApiActions.DeactivateLabSuccess, (state, { labId }) =>
    patchLabStatus(state, labId, LabStatus.INACTIVE)
  ),
  on(LabsActions.LabsApiActions.ReactivateLabSuccess, (state, { labId }) =>
    patchLabStatus(state, labId, LabStatus.ACTIVE)
  ),

  // An archiving lab leaves every regular list at once. The loaded detail stays
  // so the admin sees the teardown banner until the refetch lands.
  on(LabsActions.LabsApiActions.ArchiveLabSuccess, (state, { labId }) => ({
    ...state,
    labList: state.labList.filter((lab) => lab.id !== labId),
    selectedLab: state.selectedLab?.id === labId ? null : state.selectedLab,
    getLabDetail:
      state.getLabDetail?.id === labId
        ? { ...state.getLabDetail, status: LabStatus.ARCHIVING, active: false }
        : state.getLabDetail,
  })),

  // The row may already be gone, so nothing about the lab is kept.
  on(LabsActions.LabsApiActions.DeleteLabSuccess, (state, { labId }) => ({
    ...state,
    labList: state.labList.filter((lab) => lab.id !== labId),
    archivedLabList: state.archivedLabList.filter((lab) => lab.id !== labId),
    getLabDetail: state.getLabDetail?.id === labId ? null : state.getLabDetail,
    selectedLab: state.selectedLab?.id === labId ? null : state.selectedLab,
  })),

  on(
    LabsActions.LabsApiActions.DeactivateLabFailure,
    LabsActions.LabsApiActions.ReactivateLabFailure,
    LabsActions.LabsApiActions.ArchiveLabFailure,
    LabsActions.LabsApiActions.DeleteLabFailure,
    LabsActions.LabsApiActions.RetryLabTeardownFailure,
    (state) => ({ ...state })
  )
);

function patchLabStatus(state: LabsState, labId: number, status: LabStatus): LabsState {
  return {
    ...state,
    labList: state.labList.map((lab) => (lab.id === labId ? { ...lab, status } : lab)),
    getLabDetail:
      state.getLabDetail?.id === labId ? { ...state.getLabDetail, status } : state.getLabDetail,
  };
}

export function LabsReducer(state: LabsState | undefined, action: Action) {
  return labsReducer(state, action);
}
